import { useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { ExportToCsv } from 'export-to-csv';
import { Bar } from 'react-chartjs-2';
import DateChip from 'src/components/DateChip';
import Subtitle from 'src/components/Subtitle';
import { DebentureAssetData } from 'src/types/operations/debentures';
import { convertValue, toCurrency } from 'src/utils/number';

import csvSvg from '../../../../../assets/imgs/csv.svg';
import { formatCompetence } from '../utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip);

type MaturitySeries = 'aVencer' | 'vencidosNaoPagos';

const SERIES_CONFIG: {
  key: MaturitySeries;
  label: string;
  color: string;
}[] = [
  { key: 'aVencer', label: 'A Vencer', color: '#FF8211' },
  { key: 'vencidosNaoPagos', label: 'Vencidos e Não Pagos', color: '#1D1D1D' },
];

type AssetMaturityRangeProps = {
  data?: DebentureAssetData;
  isLoading?: boolean;
};

export default function AssetMaturityRange({
  data,
  isLoading,
}: AssetMaturityRangeProps) {
  const [isTableView, setIsTableView] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<MaturitySeries[]>([
    'aVencer',
  ]);

  const buckets = data?.maturity?.aVencer ?? [];
  const labels = buckets.map((bucket) => bucket.label);

  const handleChipToggle = (key: MaturitySeries) => {
    setSelectedSeries((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((item) => item !== key)
          : prev
        : [...prev, key],
    );
  };

  const activeSeries = SERIES_CONFIG.filter(({ key }) =>
    selectedSeries.includes(key),
  );

  const datasets = activeSeries.map(({ key, label, color }) => ({
    label,
    data: (data?.maturity?.[key] ?? []).map((bucket) => bucket.value ?? 0),
    backgroundColor: color,
  }));

  const handleExport = () => {
    const exporter = new ExportToCsv({
      fieldSeparator: ';',
      filename: 'debentures_faixa_vencimento',
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      headers: [
        'Faixa de Vencimento',
        ...activeSeries.map(({ label }) => label),
      ],
    });

    exporter.generateCsv(
      labels.map((label, index) => {
        const row: Record<string, string> = { faixa: label };
        activeSeries.forEach(({ key }) => {
          const value = data?.maturity?.[key]?.[index]?.value;
          row[key] = value != null ? toCurrency(value) : 'N/D';
        });
        return row;
      }),
    );
  };

  const chartOptions: ChartOptions<'bar'> = {
    indexAxis: 'y',
    plugins: {
      legend: { display: selectedSeries.length > 1 },
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<'bar'>) =>
            `${item.dataset.label}: ${convertValue(item.raw as number)}`,
        },
      },
    },
    scales: {
      x: { ticks: { callback: (value) => convertValue(value as number) } },
    },
    maintainAspectRatio: false,
  };

  return (
    <>
      <Subtitle
        title="Faixa de Vencimento"
        hasSelectType
        isSelectedType={isTableView}
        handleChangeType={() => setIsTableView((prev) => !prev)}
        titleType1="Gráfico"
        titleType2="Tabela"
      />
      <Paper square sx={{ padding: '24px 18px', mb: 4 }}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <DateChip
            title="Competência"
            date={formatCompetence(data?.dataReferencia)}
          />
        </Box>
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={460} />
        ) : (
          <>
            <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
              {SERIES_CONFIG.map(({ key, label }) => (
                <Grid item key={key}>
                  <Chip
                    label={label}
                    color={selectedSeries.includes(key) ? 'primary' : 'default'}
                    variant={
                      selectedSeries.includes(key) ? 'filled' : 'outlined'
                    }
                    onClick={() => handleChipToggle(key)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Grid>
              ))}
            </Grid>

            {!isTableView && (
              <Box sx={{ height: labels.length * 50 + 60 }}>
                <Bar data={{ labels, datasets }} options={chartOptions} />
              </Box>
            )}

            {isTableView && (
              <>
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ fontSize: '16px' }}
                    startIcon={
                      <Box
                        component="img"
                        src={csvSvg}
                        alt="export"
                        sx={{ width: '20px', height: '20px' }}
                      />
                    }
                    onClick={handleExport}>
                    Exportar
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>
                          Faixa de Vencimento
                        </TableCell>
                        {activeSeries.map(({ key, label }) => (
                          <TableCell
                            key={key}
                            align="right"
                            sx={{ fontWeight: 600, fontSize: '12px' }}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {labels.map((label, index) => (
                        <TableRow key={label}>
                          <TableCell sx={{ fontSize: '0.8em' }}>
                            {label}
                          </TableCell>
                          {activeSeries.map(({ key }) => {
                            const value = data?.maturity?.[key]?.[index]?.value;
                            return (
                              <TableCell
                                key={key}
                                align="right"
                                sx={{ fontSize: '0.8em' }}>
                                {value != null ? toCurrency(value) : 'N/D'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  );
}
